import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';

interface SubscriptionPlan {
  id: number;
  plan_name: string;
  number_of_tokens: number;
  max_users: number;
}


const SuperuserOnboarding: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [additionalUserPasswords, setAdditionalUserPasswords] = useState<
    Array<{ email: string; temp_password: string }>
  >([]);

  const [orgName, setOrgName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminTitle, setAdminTitle] = useState('');
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<number | ''>('');
  const [tokenCount, setTokenCount] = useState<number>(0);
  const [trialDays, setTrialDays] = useState<number>(14);
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [allowFdaApprovals, setAllowFdaApprovals] = useState(true);
  const [adminIsAcademic, setAdminIsAcademic] = useState(false);
  const [additionalUsers, setAdditionalUsers] = useState<Array<{ email: string; title: string; is_academic: boolean }>>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [planData] = await Promise.all([apiService.fetchSubscriptionPlans()]);
        setPlans(planData);
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || 'Failed to load onboarding data.');
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!subscriptionPlanId) {
      return;
    }
    const plan = plans.find((p) => p.id === subscriptionPlanId);
    if (plan && tokenCount === 0) {
      setTokenCount(plan.number_of_tokens);
    }
  }, [plans, subscriptionPlanId, tokenCount]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setTempPassword(null);
    setLoading(true);
    try {
      const payload = {
        org_name: orgName.trim(),
        admin_email: adminEmail.trim(),
        admin_title: adminTitle.trim(),
        admin_is_academic: adminIsAcademic,
        plan_id: subscriptionPlanId,
        token_count: tokenCount,
        trial_days: trialDays,
        trial_enabled: trialEnabled,
        allow_fda_approvals: allowFdaApprovals,
        additional_users: additionalUsers
          .map((user) => ({ email: user.email.trim(), title: user.title.trim(), is_academic: user.is_academic }))
          .filter((user) => user.email),
      };
      const result = await apiService.createTrialOnboarding(payload);
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create onboarding record.');
      }
      setSuccess('Demo trial users created successfully.');
      setTempPassword(result?.temp_password || null);
      setAdditionalUserPasswords(result?.additional_users || []);
      setOrgName('');
      setAdminEmail('');
      setAdminTitle('');
      setSubscriptionPlanId('');
      setTokenCount(0);
      setTrialDays(14);
      setTrialEnabled(true);
      setAllowFdaApprovals(true);
      setAdminIsAcademic(false);
      setAdditionalUsers([]);
      setAdditionalUserPasswords([]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to create demo trial.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
        <h1 className="text-2xl font-bold text-slate-900">Demo Trial Onboarding</h1>
        <p className="text-sm text-slate-500 mt-1">Create an org, admin user, and limited trial access in one flow.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="space-y-2 text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Organization Name</span>
              <input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                required
              />
            </label>
            <label className="space-y-2 text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subscription Plan</span>
              <select
                value={subscriptionPlanId}
                onChange={(e) => setSubscriptionPlanId(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
                required
              >
                <option value="">Select a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.plan_name} (tokens: {plan.number_of_tokens}, users: {plan.max_users})
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-2 text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Token Count</span>
              <input
                type="number"
                value={tokenCount}
                onChange={(e) => setTokenCount(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                min={0}
              />
            </label>
            <label className="space-y-2 text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Access Duration (days)
              </span>
              <input
                type="number"
                value={trialDays}
                onChange={(e) => setTrialDays(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                min={1}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="space-y-2 text-sm text-slate-600 flex items-center gap-3">
              <input
                type="checkbox"
                checked={trialEnabled}
                onChange={(e) => setTrialEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600"
              />
              <span className="text-sm text-slate-600">Demo trial access</span>
            </label>
            <label className="space-y-2 text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Admin Email</span>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                required
              />
            </label>
            <label className="space-y-2 text-sm text-slate-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Admin Title</span>
              <input
                value={adminTitle}
                onChange={(e) => setAdminTitle(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-2 text-sm text-slate-600 flex items-center gap-3">
              <input
                type="checkbox"
                checked={adminIsAcademic}
                onChange={(e) => setAdminIsAcademic(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600"
              />
              <span className="text-sm text-slate-600">Academic user</span>
            </label>
            <label className="space-y-2 text-sm text-slate-600 flex items-center gap-3">
              <input
                type="checkbox"
                checked={allowFdaApprovals}
                onChange={(e) => setAllowFdaApprovals(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600"
              />
              <span className="text-sm text-slate-600">Allow FDA approvals access</span>
            </label>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">Additional Users (non-admin)</p>
                <p className="text-xs text-slate-500">These users will also receive welcome emails.</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setAdditionalUsers((prev) => [...prev, { email: '', title: '', is_academic: false }])
                }
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Add user
              </button>
            </div>
            {additionalUsers.length > 0 && (
              <div className="space-y-2">
                {additionalUsers.map((user, index) => (
                  <div key={`extra-user-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto,auto] gap-3">
                    <input
                      type="email"
                      value={user.email}
                      onChange={(e) => {
                        const next = [...additionalUsers];
                        next[index] = { ...next[index], email: e.target.value };
                        setAdditionalUsers(next);
                      }}
                      placeholder="user@email.com"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    <input
                      value={user.title}
                      onChange={(e) => {
                        const next = [...additionalUsers];
                        next[index] = { ...next[index], title: e.target.value };
                        setAdditionalUsers(next);
                      }}
                      placeholder="Title (optional)"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={user.is_academic}
                        onChange={(e) => {
                          const next = [...additionalUsers];
                          next[index] = { ...next[index], is_academic: e.target.checked };
                          setAdditionalUsers(next);
                        }}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                      />
                      Academic
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setAdditionalUsers((prev) => prev.filter((_, i) => i !== index))
                      }
                      className="px-3 py-2 rounded-lg text-xs font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>


          {error && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 space-y-1">
              <p>{success}</p>
              {tempPassword && (
                <p className="text-xs text-emerald-800">
                  Admin temporary password: <span className="font-semibold">{tempPassword}</span>
                </p>
              )}
              {additionalUserPasswords.length > 0 && (
                <div className="text-xs text-emerald-800 space-y-1">
                  <p className="font-semibold">Additional user passwords:</p>
                  {additionalUserPasswords.map((item) => (
                    <p key={item.email}>
                      {item.email}: <span className="font-semibold">{item.temp_password}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? 'Creating…' : 'Create demo trial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuperuserOnboarding;
